import { useState } from "react";
import { ticketService } from "@/services/ticketService";
import { ticketLinkService } from "@/services/ticketLinkService";
import { formatError } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

export function LinkedTicketsPanel({ links, user, navigate, ticketId, fetchLinks, refreshActivity }) {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkTargetId, setLinkTargetId] = useState("");
  const [linkType, setLinkType] = useState("relates_to");
  const [allTickets, setAllTickets] = useState([]);

  const openLinkDialog = async () => {
    try { const { data } = await ticketService.list({ limit: 100 }); setAllTickets(data.tickets.filter((t) => t.id !== ticketId)); } catch { toast.error("Failed to load tickets for linking"); }
    setLinkDialogOpen(true);
  };

  const handleCreateLink = async () => {
    if (!linkTargetId) { toast.error("Select a target ticket"); return; }
    try {
      await ticketLinkService.create(ticketId, { target_ticket_id: linkTargetId, link_type: linkType });
      toast.success("Link created"); setLinkDialogOpen(false); setLinkTargetId(""); fetchLinks();
      await refreshActivity();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleDeleteLink = async (linkId) => {
    try { await ticketLinkService.delete(ticketId, linkId); toast.success("Link removed"); fetchLinks(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <>
      <Card className="border-[#E4E4E7] shadow-none rounded-sm" data-testid="links-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-sm font-bold tracking-tight">Linked Tickets ({links.length})</CardTitle>
            {user?.role !== "end_user" && (
              <Button variant="ghost" size="sm" onClick={openLinkDialog} className="rounded-sm text-xs h-7 gap-1" data-testid="add-link-btn"><Plus size={12} /> Link</Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {links.length === 0 ? (
            <p className="text-xs text-[#52525B]" data-testid="no-links">No linked tickets</p>
          ) : (
            <div className="space-y-1.5">
              {links.map((link) => (
                <div key={link.id} className="flex items-center gap-1.5 text-xs" data-testid={`link-${link.id}`}>
                  <span className="uppercase tracking-wider text-[10px] font-bold text-[#52525B] bg-zinc-100 px-1 py-0.5 rounded-sm whitespace-nowrap">{link.link_type?.replace("_", " ")}</span>
                  <button onClick={() => navigate(`/tickets/${link.linked_ticket?.id}`)} className="text-[#003DA5] hover:underline truncate flex-1 text-left" data-testid={`link-navigate-${link.id}`}>
                    {link.linked_ticket?.ticket_number} {link.linked_ticket?.title}
                  </button>
                  {user?.role !== "end_user" && (
                    <button onClick={() => handleDeleteLink(link.id)} className="text-red-500 hover:text-red-600 p-0.5 shrink-0" data-testid={`delete-link-${link.id}`}><X size={11} /></button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Link Ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#52525B]">Link Type</Label>
              <Select value={linkType} onValueChange={setLinkType}>
                <SelectTrigger className="h-8 rounded-sm text-sm" data-testid="link-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="relates_to">Relates to</SelectItem>
                  <SelectItem value="blocks">Blocks</SelectItem>
                  <SelectItem value="blocked_by">Blocked by</SelectItem>
                  <SelectItem value="duplicate_of">Duplicate of</SelectItem>
                  <SelectItem value="parent_of">Parent of</SelectItem>
                  <SelectItem value="child_of">Child of</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#52525B]">Target Ticket</Label>
              <Select value={linkTargetId || "pick"} onValueChange={(v) => setLinkTargetId(v === "pick" ? "" : v)}>
                <SelectTrigger className="h-8 rounded-sm text-sm" data-testid="link-target-select"><SelectValue placeholder="Select ticket..." /></SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  <SelectItem value="pick" disabled>Select ticket...</SelectItem>
                  {allTickets.map((t) => <SelectItem key={t.id} value={t.id}>{t.ticket_number} - {t.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleCreateLink} className="rounded-sm bg-[#003DA5] hover:bg-[#002D7A] text-white" data-testid="confirm-link-btn">Create Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
